<script setup lang="ts">
import { computed, defineComponent, h } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { InputBox, PopupBasicHeader } from 'wepin-vue-component'
import CreateTokenNetwork from './CreateTokenNetwork.vue'
import {
  BasicBottomButtons as BBB,
  WrappedTokenListItem,
  NoticeText,
} from '@/components'
import { useBreakPointsClass, useInput, useKeyDown } from '@/composables'
import { defaultBreakPoints } from '@/const'
import { useCoinStore, useWalletStore } from '@/stores'
import type { Coin, Promisable } from '@/types'

const emits = defineEmits<{
  (event: 'close'): void
  (event: 'click-confirm', label: string): void
}>()

const { t } = useI18n()

const { breakPointsClass } = useBreakPointsClass(defaultBreakPoints)
const walletStore = useWalletStore()
const coinStore = useCoinStore()
const router = useRouter()
const { isError, inputValue: label, onInputLabel } = useInput()

const selectedCoin = computed(() => {
  const coin: Coin | undefined = coinStore.coinList!.find(
    (coin) =>
      coin.id.toString() === router.currentRoute.value.query.selectedCoin,
  )
  return coin
})
const selectedParentAccount = computed(() => {
  if (router.currentRoute.value.query.selectedToken) {
    const account = walletStore.getAccount({
      accountId: router.currentRoute.value.query.selectedToken as string,
    })
    return account
  }
  return null
})

const onExit = () => {
  emits('close')
}

const onClickConfirm = () => {
  if (isError.value) return
  emits('click-confirm', label.value)
}
useKeyDown({ confirm: onClickConfirm })
</script>
<script lang="ts">
export const createTokenNetwork = (params: {
  onClickConfirm: (label: string) => Promisable<void>
}) => {
  return defineComponent<typeof CreateTokenNetwork>((props) => {
    return () =>
      h(CreateTokenNetwork, {
        'onClick-confirm': params.onClickConfirm,
        ...props,
      })
  })
}
</script>
<template>
  <PopupBasicHeader :title="t('create_account.add_account')" :exit="onExit" />
  <div class="create-token-network" :class="[breakPointsClass]">
    <div class="notice-text">
      <i18n-t :keypath="'create_account.network_for_tokens_title'">
        <template #line_break>
          <br />
        </template>
      </i18n-t>
      <span>
        <i18n-t :keypath="'create_account.network_for_tokens_sub'">
          <template #line_break>
            <br />
          </template>
        </i18n-t>
      </span>
    </div>
    <WrappedTokenListItem
      class="token-list-item"
      :ticker="selectedCoin?.name"
      :symbol="selectedCoin?.symbol"
      :ticker-src="selectedCoin?.iconUrl"
      :network-src="selectedParentAccount?.coin?.iconUrl"
    />
    <InputBox
      width="100%"
      :placeholder="t('create_account.name_placeholder')"
      :model-value="label"
      :clear="true"
      :error="isError"
      maxlength="14"
      @update:model-value="onInputLabel"
    />
    <NoticeText class="notice-wrapper" :is-error="isError">
      {{ t('create_account.input_name_caution') }}
    </NoticeText>
    <BBB
      class="default-margin"
      :disabled="isError"
      @confirm="onClickConfirm"
      @cancel="onExit"
    />
  </div>
</template>

<style lang="scss" scoped>
@import 'wepin-vue-component/src/styles/typeface.module.scss';

.create-token-network {
  padding: 0 30px;
  max-width: 460px;
  .notice-text {
    @include typeface('Title_18');
    & > span {
      display: block;
      margin-top: 10px;
      @include typeface('Paragraph_16_r');
    }
  }

  .notice-wrapper {
    background-color: transparent;
    margin-top: 10px;
    margin-bottom: 20px;
    padding: initial;
  }
  .token-list-item {
    padding: {
      left: 0;
      right: 0;
    }
  }

  .buttons {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 30px;
  }

  .bbb {
    margin-bottom: 30px;
  }
}

.create-token-network.mobile {
  padding: 0 20px;
}
</style>
